import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../SEO/SEO';
import ArticleSchema from '../SEO/ArticleSchema';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import NewsletterSignup from '../NewsletterSignup/NewsletterSignup';
import ArticleCard from '../ArticleCard/ArticleCard';
import articles from '../Articles/articles';
import { Calendar, Clock, Facebook, Twitter, Linkedin } from 'lucide-react';
import { trackArticleView, trackSocialShare } from '../../utils/analytics';
import '../Styles/reset.css';
import '../Styles/utils.css';
import '../Styles/layout.css';

const Article = () => {
    const { articleId } = useParams(); // 👈 Get articleId from URL
    const [article, setArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);

    useEffect(() => {
        const currentArticle = articles.find(
            a => a.id.toString() === articleId // match as string to be safe
        );

        if (currentArticle) {
            setArticle(currentArticle);

            trackArticleView(currentArticle);

            const related = articles
                .filter(
                    a => a.category === currentArticle.category && a.id !== currentArticle.id
                )
                .slice(0, 3);

            setRelatedArticles(related);
        }
    }, [articleId]);

    if (!article) {
        return <div>Loading...</div>;
    }

    const currentUrl = `/article/${articleId}`;
    const articleImageUrl = article.featuredImage || 'https://smartlifechoice.co.uk/logo-og.png';

    return (
        <div>
            <SEO
                title={`${article.title} - SmartLifeChoice`}
                description={article.excerpt}
                url={currentUrl}
                image={articleImageUrl}
                article={true}
                author={article.author}
                publishedTime={article.datePublished}
                modifiedTime={article.dateModified}
                category={article.category}
                tags={article.tags}
                keywords={article.tags ? article.tags.join(', ') : ''}
            />
            <ArticleSchema article={article} />
            <Header />

            <main className="main">
                <div className="container">
                    <article>
                        <header className="article-header">
                            <span className="article-category">{article.category}</span>
                            <h1>{article.title}</h1>

                            <div className="article-header-meta">
                                <div className="article-author">
                                    <div className="author-avatar">
                                        <img src={article.authorImage} alt={`${article.author} profile`} loading="lazy" />
                                    </div>
                                    <span>{article.author}</span>
                                </div>

                                <div className="article-date">
                                    <Calendar size={16} />
                                    <span>{article.date}</span>
                                </div>

                                <div className="article-date">
                                    <Clock size={16} />
                                    <span>{article.readTime}</span>
                                </div>
                            </div>
                        </header>

                        <div className="article-featured-image">
                            <img src={article.featuredImage} alt={article.title} loading="lazy" />
                        </div>

                        <div
                            className="article-body"
                            dangerouslySetInnerHTML={{ __html: article.content }}
                        />

                        <div className="article-tags">
                            {article.tags.map((tag, index) => (
                                <span key={index} className="article-tag">#{tag}</span>
                            ))}
                        </div>

                        <div className="share-buttons">
                            <span>Share:</span>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://smartlifechoice.co.uk' + currentUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="share-button"
                                style={{ backgroundColor: '#3b5998' }}
                                onClick={() => trackSocialShare('Facebook', article.title)}
                                aria-label="Share on Facebook"
                            >
                                <Facebook size={18} />
                            </a>
                            <a
                                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent('https://smartlifechoice.co.uk' + currentUrl)}&text=${encodeURIComponent(article.title)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="share-button"
                                style={{ backgroundColor: '#1da1f2' }}
                                onClick={() => trackSocialShare('Twitter', article.title)}
                                aria-label="Share on Twitter"
                            >
                                <Twitter size={18} />
                            </a>
                            <a
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://smartlifechoice.co.uk' + currentUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="share-button"
                                style={{ backgroundColor: '#0077b5' }}
                                onClick={() => trackSocialShare('LinkedIn', article.title)}
                                aria-label="Share on LinkedIn"
                            >
                                <Linkedin size={18} />
                            </a>
                        </div>
                    </article>

                    <section className="related-articles">
                        <h2 className="section-title">Related Articles</h2>
                        <div className="featured-grid">
                            {relatedArticles.map(article => (
                                <ArticleCard key={article.id} article={article} />
                            ))}
                        </div>
                    </section>
                </div>

                <NewsletterSignup />
            </main>

            <Footer />
        </div>
    );
};

export default Article;
